import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { VERSION } from '../version';
import './Landing.css';

function Landing() {
  const [formStatus, setFormStatus] = useState('idle'); // idle, sending, success, error
  const features = [
    {
      icon: '👥',
      title: 'Gestión completa de clientes',
      description: 'Centraliza toda la información de tus clientes en un solo lugar. Accede rápidamente a sus datos, objetivos, historial y preferencias. Nunca más perderás información importante.'
    },
    {
      icon: '📅',
      title: 'Agenda visual intuitiva',
      description: 'Gestiona tu calendario de forma visual y eficiente. Programa citas con un solo clic, gestiona modalidades presenciales y online, y nunca olvides una cita importante.'
    },
    {
      icon: '💬',
      title: 'Registro detallado de sesiones',
      description: 'Documenta cada sesión de forma profesional. Registra objetivos, contexto, observaciones y seguimiento. Mantén un historial completo del progreso de cada cliente.'
    },
    {
      icon: '📄',
      title: 'Documentos profesionales automáticos',
      description: 'Genera informes y documentos personalizados en segundos. Plantillas Word editables que se adaptan a tu marca y estilo profesional.'
    },
    {
      icon: '📊',
      title: 'Análisis y métricas en Tiempo Real',
      description: 'Conoce el estado de tus horas prácticas al instante. Visualiza clientes activos, sesiones completadas, tiempo invertido y métricas clave para tomar decisiones informadas.'
    },
    {
      icon: '🔍',
      title: 'Búsqueda instantánea',
      description: 'Encuentra cualquier información en segundos. Búsqueda rápida y potente que te ahorra tiempo y mejora tu productividad.'
    },
    {
      icon: '🔒',
      title: 'Seguridad y privacidad garantizadas',
      description: 'Tus datos están protegidos con copias de seguridad automáticas y sistema de seguridad robusto. Cumple con las normativas de protección de datos.'
    },
    {
      icon: '📱',
      title: 'Acceso desde cualquier dispositivo',
      description: 'Trabaja desde donde quieras, cuando quieras. Plataforma web responsive que funciona en ordenadores, tablets o móviles.'
    },
    {
      icon: '📋',
      title: 'Facturación simplificada',
      description: 'Gestiona la facturación de tus clientes de forma sencilla. Registra pagos, genera facturas y mantén un control de tus ingresos.'
    }
  ];

  const benefits = [
    {
      icon: '⚡',
      title: 'Ahorra tiempo y aumenta tu productividad',
      description: 'Reduce el tiempo administrativo en un 70%. Dedica más tiempo a lo que realmente importa: tus clientes.'
    },
    {
      icon: '💰',
      title: 'Aumenta tus ingresos',
      description: 'Mejorar la organización te permite atender más clientes y ofrecer un servicio más profesional que justifica mejores tarifas.'
    },
    {
      icon: '⭐',
      title: 'Profesionaliza tu práctica',
      description: 'Sorprende a tus clientes con una gestión profesional. Genera documentos de calidad y demuestra tu compromiso con su éxito.'
    },
    {
      icon: '📈',
      title: 'Toma decisiones basadas en datos',
      description: 'Analiza el rendimiento de tus horas prácticas con métricas claras. Identifica oportunidades de crecimiento y optimiza tu negocio.'
    }
  ];

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="landing-hero">
        <div className="hero-content">
          <img 
            src="/coachflow-logo.png" 
            alt="COACHFLOW Logo" 
            className="hero-logo"
          />
          <p className="hero-subtitle">
            La Plataforma que transforma tus prácticas de Coaching
          </p>
          <p className="hero-description">
            Gestiona clientes, sesiones y documentos de forma profesional. Ahorra tiempo, aumenta tu productividad y lleva tus horas prácticas al siguiente nivel.
          </p>
          <div className="hero-cta">
            <a href="/login.php" className="btn btn-primary btn-large">
              Comenzar Ahora
            </a>
            <a href="#features" className="btn btn-secondary btn-large">
              Conocer Más
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="landing-features">
        <div className="container">
          <h2 className="section-title">Todo lo que necesitas en un solo lugar</h2>
          <p className="section-subtitle">
            Una plataforma completa diseñada específicamente para coaches profesionales
          </p>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="landing-benefits">
        <div className="container">
          <h2 className="section-title">Beneficios reales para tu negocio</h2>
          <p className="section-subtitle">
            Descubre cómo COACHFLOW puede transformar tus horas prácticas profesionales
          </p>
          <div className="benefits-list">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-item">
                <div className="benefit-icon">{benefit.icon}</div>
                <div className="benefit-content">
                  <h3>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="landing-stats">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">70%</div>
              <div className="stat-label">Menos tiempo administrativo</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Datos seguros </div>
            </div>
            <div className="stat-item">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Acceso desde cualquier lugar</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">∞</div>
              <div className="stat-label">Clientes y sesiones ilimitadas</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-cta">
        <div className="container">
          <h2 className="cta-title">¿Listo para transformar tus horas prácticas?</h2>
          <p className="cta-description">
            Únete a coaches profesionales que ya están usando COACHFLOW para llevar su negocio al siguiente nivel. 
            Comienza hoy mismo y experimenta la diferencia.
          </p>
          <div className="cta-buttons">
            <a href="/login.php" className="btn btn-primary btn-large">
              Acceder Ahora
            </a>
            <a href="#features" className="btn btn-outline btn-large">
              Ver Funcionalidades
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contacto" className="landing-contact">
        <div className="container">
          <h2 className="section-title">Contacta con nosotros</h2>
          <p className="section-subtitle">
            ¿Tienes preguntas o quieres saber más? Escríbenos y te responderemos lo antes posible.
          </p>

          {formStatus === 'success' ? (
            <div className="contact-success">
              <div className="contact-success-icon">✅</div>
              <h3>¡Gracias por tu mensaje!</h3>
              <p>Hemos recibido tu consulta correctamente. Te responderemos lo antes posible.</p>
              <button
                className="btn btn-secondary"
                onClick={() => setFormStatus('idle')}
                style={{ marginTop: '16px' }}
              >
                Enviar otro mensaje
              </button>
            </div>
          ) : (
            <form
              className="contact-form"
              onSubmit={async (e) => {
                e.preventDefault();
                setFormStatus('sending');
                try {
                  const formData = new FormData(e.target);
                  const res = await fetch('https://formspree.io/f/maqdykbk', {
                    method: 'POST',
                    body: formData,
                    headers: { Accept: 'application/json' },
                  });
                  if (res.ok) {
                    setFormStatus('success');
                    e.target.reset();
                  } else {
                    setFormStatus('error');
                  }
                } catch {
                  setFormStatus('error');
                }
              }}
            >
              <div className="contact-form-grid">
                <div className="form-group">
                  <label htmlFor="contact-name">Nombre *</label>
                  <input
                    id="contact-name"
                    type="text"
                    name="nombre"
                    required
                    placeholder="Tu nombre"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="contact-email">Email *</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    placeholder="tu@email.com"
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="contact-subject">Asunto</label>
                <input
                  id="contact-subject"
                  type="text"
                  name="asunto"
                  placeholder="¿En qué podemos ayudarte?"
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-message">Mensaje *</label>
                <textarea
                  id="contact-message"
                  name="mensaje"
                  required
                  rows="5"
                  placeholder="Escribe tu mensaje aquí..."
                />
              </div>
              {formStatus === 'error' && (
                <div style={{ color: '#e74c3c', marginBottom: '12px', fontSize: '14px' }}>
                  Ha ocurrido un error al enviar el mensaje. Inténtalo de nuevo.
                </div>
              )}
              <button
                type="submit"
                className="btn btn-primary btn-large"
                disabled={formStatus === 'sending'}
                style={{ width: '100%' }}
              >
                {formStatus === 'sending' ? 'Enviando...' : 'Enviar mensaje'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="app-footer">
        <p>
          &copy; 2025-26 CdL. All rights reserved. | Made with Love in Spain | v{VERSION}
          <span className="footer-links">
            {' | '}<Link to="/privacidad" style={{ color: 'inherit', opacity: 0.8 }}>Política de Privacidad</Link>
            {' | '}<Link to="/terminos" style={{ color: 'inherit', opacity: 0.8 }}>Términos y Condiciones</Link>
          </span>
        </p>
      </footer>
    </div>
  );
}

export default Landing;

